import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Hooks
import { useAppSelector, useAppDispatch } from "app/hooks";

// Store
import { getUseNewFunctionalData, toggleLoader } from "store/slice/UI";
import { deletePerson } from "store/slice/Team";
import { getPaceLabsLspm, fetchPaceLabsLSPM } from "store/slice/PaceLabs/paceLabsLSPM";
import { fetchPaceLabsCharts, getPaceLabsChartsAll } from "store/slice/PaceLabs/paceLabsCharts";

// Services
import { fetchPersonWorkHistory, updateWorkHistoryService } from "services/people/people.service";

// Utils
import { generateID, sortWorkHistoryAddFromTo } from "utils";
import {
    convertWorkHistoryBeforeSave,
    convertWorkHistorytoWHEntry,
    updateWorkHistory,
} from "components/SetupWizard/SetupWizard.functions";

// Types
import { WorkHistoryData } from "services/people/people.types";
import { WHEntry } from "components/SetupWizard/SetupWizard.types";
import { RequestInfo } from "store/slice/store.types";

// Consts
import { defaultWHEntry } from "components/SetupWizard/useSetupWizard";
import { PACE_LABS_CHARTS } from "constants/constants";
import { urlPaths } from "enums/urlPaths";

const useYourProfile = (
    personId: number | undefined
): {
    wrapperRef: React.RefObject<HTMLDivElement | null>;
    scrollerWrapperRef: React.RefObject<HTMLDivElement>;
    newestElementRef: React.RefObject<HTMLDivElement>;
    workHistoryData: { data: WorkHistoryData[]; requestInfo: RequestInfo };
    LSPMRequestPaceInfo: RequestInfo;
    chartsRequestInfo: RequestInfo;
    showModal: boolean;
    isNewEntry: boolean;
    whEntries: WHEntry[];
    selectedEntryIdx: number;
    isWHEntryValid: (entry: WHEntry) => boolean;
    setWHEntries: React.Dispatch<React.SetStateAction<WHEntry[]>>;
    handleNavigateToMyExperience: () => void;
    handleEditEntry: (position: number) => void;
    handleAddEntry: () => void;
    handleCancelChanges: () => Promise<void>;
    handleSaveChanges: () => Promise<void>;
    handleDeleteEntry: () => Promise<void>;
} => {
    const location = useLocation();
    const navigate = useNavigate();

    const dispatch = useAppDispatch();
    const { paceDataStatus: LSPMRequestPaceInfo } = useAppSelector(getPaceLabsLspm);
    const { status: chartsRequestInfo } = useAppSelector(getPaceLabsChartsAll);

    const graphingByFunction = useAppSelector(getUseNewFunctionalData);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const scrollerWrapperRef = useRef<HTMLDivElement>(null);
    const newestElementRef = useRef<HTMLDivElement>(null);
    const initialEntries = useRef<WHEntry[]>([]);
    const currentEntries = useRef<WHEntry[]>([]);

    const [whEntries, setWHEntries] = useState<WHEntry[]>([]);
    const [selectedEntryIdx, setSelectedEntryIdx] = useState<number>(-1);
    const [isNewEntry, setIsNewEntry] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const [workHistoryData, setworkHistoryData] = useState<{
        data: WorkHistoryData[];
        requestInfo: RequestInfo;
    }>({
        data: [],
        requestInfo: "pristine",
    });

    useEffect(() => {
        const handleFetchWorkHistory = async () => {
            if (!personId || workHistoryData.data.length || workHistoryData.requestInfo !== "pristine") {
                return;
            }

            setworkHistoryData((currentVal) => ({ ...currentVal, requestInfo: "fetching" }));

            try {
                const { data } = await fetchPersonWorkHistory(personId);
                const sortedWorkHistory = sortWorkHistoryAddFromTo(data, "tenure_to");

                if (sortedWorkHistory?.length) {
                    const convertedWH = convertWorkHistorytoWHEntry(sortedWorkHistory);
                    initialEntries.current = convertedWH;
                    setworkHistoryData((currentVal) => ({ ...currentVal, data: sortedWorkHistory }));
                    setWHEntries(convertedWH);
                }
                setworkHistoryData((currentVal) => ({ ...currentVal, requestInfo: "done" }));
            } catch (error) {
                console.error(error);
                setworkHistoryData((currentVal) => ({ ...currentVal, requestInfo: "error" }));
            }
        };

        handleFetchWorkHistory();
    }, [personId, workHistoryData, chartsRequestInfo, LSPMRequestPaceInfo, location.pathname]);

    // Scroll to modified item in the work history list
    useLayoutEffect(() => {
        if (scrollerWrapperRef.current && newestElementRef.current) {
            scrollerWrapperRef.current.scrollTop =
                newestElementRef.current.offsetTop - scrollerWrapperRef.current.offsetTop;

            setworkHistoryData((currentVal) => ({
                ...currentVal,
                data: currentVal.data.map(({ isLastEntry, ...val }) => val),
            }));
        }
    }, [workHistoryData]);

    // Modal actions buttons
    const handleCloseModal = () => {
        setShowModal(false);
        setIsNewEntry(false);
        setSelectedEntryIdx(-1);
    };

    const handleSaveChanges = async () => {
        dispatch(toggleLoader(true));
        handleCloseModal();

        const updatedWh = updateWorkHistory(workHistoryData.data, whEntries, true);
        const sortedWorkHistory = sortWorkHistoryAddFromTo(updatedWh, "tenure_to") || [];
        const convertedWH = convertWorkHistorytoWHEntry(sortedWorkHistory);

        setworkHistoryData((currentVal) => ({ ...currentVal, data: sortedWorkHistory }));
        setWHEntries(convertedWH);

        await handleUpdateWorkHistory(updatedWh);
        await dispatch(fetchPaceLabsLSPM({ person_id: personId as number, pace: true }));
        await dispatch(fetchPaceLabsCharts({ charts: PACE_LABS_CHARTS, graphingByFunction }));
        await dispatch(deletePerson(personId as number));

        dispatch(toggleLoader(false));
    };

    const handleDeleteEntry = async () => {
        dispatch(toggleLoader(true));
        handleCloseModal();

        setWHEntries((currentEntries) => currentEntries.filter((entry, i) => i !== selectedEntryIdx));
        setworkHistoryData((currentVal) => ({
            ...currentVal,
            data: currentVal.data.filter((wh, i) => i !== selectedEntryIdx),
        }));

        const updatedWh = workHistoryData.data.filter((wh, i) => i !== selectedEntryIdx);

        await handleUpdateWorkHistory(updatedWh);
        await dispatch(fetchPaceLabsLSPM({ person_id: personId as number, pace: true }));
        await dispatch(fetchPaceLabsCharts({ charts: PACE_LABS_CHARTS, graphingByFunction }));
        await dispatch(deletePerson(personId as number));

        dispatch(toggleLoader(false));
    };

    const handleCancelChanges = async () => {
        setWHEntries(currentEntries.current);
        handleCloseModal();
    };

    // Your Work History list actions buttons
    const handleAddEntry = () => {
        currentEntries.current = whEntries;
        setWHEntries((currentEntries) => [...currentEntries, { ...defaultWHEntry, id: generateID() }]);
        setSelectedEntryIdx(whEntries.length);
        setIsNewEntry(true);
        setShowModal(true);
    };

    const handleEditEntry = (position: number) => {
        currentEntries.current = whEntries;
        setSelectedEntryIdx(position);
        setShowModal(true);
    };

    // Utils
    const isWHEntryValid = (entry: WHEntry) =>
        Boolean(
            entry &&
                entry?.workPosition &&
                entry?.companyName &&
                entry?.startDate.value &&
                (entry?.endDate.value || entry?.endDate.isPresent)
        );

    const handleUpdateWorkHistory = async (workHistoryData: WorkHistoryData[]) => {
        const work_history = convertWorkHistoryBeforeSave(workHistoryData);
        let response;

        if (personId) {
            try {
                response = await updateWorkHistoryService({
                    person_id: personId,
                    work_history,
                });
            } catch (error) {
                console.error(error);
            }
        }

        if (response?.status === 200) {
            initialEntries.current = whEntries;
        }
    };

    const handleNavigateToMyExperience = () => {
        navigate(urlPaths.MyExperience);
    };
    return {
        wrapperRef,
        scrollerWrapperRef,
        newestElementRef,
        workHistoryData,
        LSPMRequestPaceInfo,
        chartsRequestInfo,
        showModal,
        isNewEntry,
        whEntries,
        selectedEntryIdx,
        isWHEntryValid,
        setWHEntries,
        handleNavigateToMyExperience,
        handleEditEntry,
        handleAddEntry,
        handleCancelChanges,
        handleSaveChanges,
        handleDeleteEntry,
    };
};

export default useYourProfile;
